import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useDashboard } from '@/contexts/DashboardContext'
import { useWorkspaceClient } from '@/hooks/useWorkspaceClient'
import { useQuery } from '@tanstack/react-query'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import { Search, LogOut, Settings, ChevronDown, LayoutDashboard, History } from 'lucide-react'
import { useDashboardLayout } from '@/components/new-ux/shared/useDashboardLayout'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { OldPagesList } from './OldPagesDropdown'

interface NavItem {
  label: string
  path: string
  matchPaths: string[]
  /** When true, render as a normal <a target="_blank"> — for tabs that
   *  point at an external tool rather than an internal route. */
  external?: boolean
  /** When true, hide this item from non-admin users (e.g. client role).
   *  The matching route is also gated with requireAdmin so URL-hacking
   *  redirects, but the nav still shouldn't tease the link. */
  adminOnly?: boolean
  /** When true, render a small "coming soon" badge next to the label. */
  comingSoon?: boolean
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', matchPaths: ['/', '/dashboard'] },
  { label: 'TAM / CRM', path: '/tam-crm/accounts', matchPaths: ['/tam-crm'] },
  // Order follows the work (Jamie, 27 Oct 2026): who to email, can we send, how it's going, who replied, what became a deal.
  { label: 'Deliverability', path: '/deliverability', matchPaths: ['/deliverability'], adminOnly: true },
  { label: 'Campaign Performance', path: '/campaigns', matchPaths: ['/campaigns'] },
  { label: 'Reply Queue', path: '/reply-queue', matchPaths: ['/reply-queue'], adminOnly: true },
  // Our own full funnel: positive reply → meeting → deal → onboarded. Internal only, not offered to clients yet.
  { label: 'Pipeline', path: '/pipeline', matchPaths: ['/pipeline', '/sales'], adminOnly: true },
  // Oct 2026: MasterInbox is embedded on /master-inbox (per-user MI member,
  // see MasterInboxEmbed). Was an external link to app.masterinbox.com.
  { label: 'Master Inbox', path: '/master-inbox', matchPaths: ['/master-inbox'] },
  { label: 'Team', path: '/team', matchPaths: ['/team'], adminOnly: true },
  // Settings lives in the profile menu (top right), not the header.
]

export function NewNavbar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { profile, signOut, isAdmin } = useAuth()
  // Dashboard layout switch (admin-only; DECISIONS_LOG "Dashboard v2 layout").
  const dashboardLayout = useDashboardLayout()
  const { state, setSelectedClient } = useDashboard()
  const workspace = useWorkspaceClient()
  const [clientSearchTerm, setClientSearchTerm] = useState('')
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false)
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)
  const [oldPagesOpen, setOldPagesOpen] = useState(false)

  const { data: clients = [] } = useQuery({
    // -v2 bump (Jamie 2026-05-10) — old cached entries from before the
    // status='Active' filter included churned workspaces. Bump invalidates.
    // -v3: OutboundBuddy itself is now in the list (its own deals and tasks on Pipeline).
    queryKey: ['active-clients-navbar-v3'],
    queryFn: async () => {
      if (!isSupabaseConfigured() || !supabase) return []
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, status')
        .eq('status', 'Active')
        .neq('id', 'default')
        .not('name', 'ilike', '%global%default%keys%')
        .not('name', 'ilike', '%default%keys%')
        .order('name')
      if (error) throw error
      return data || []
    },
    enabled: true,
  })

  const filteredClients = clientSearchTerm.trim()
    ? clients.filter((c: any) => c.name.toLowerCase().includes(clientSearchTerm.toLowerCase()))
    : clients

  const selectedClient = state.selectedClient === 'all'
    ? { name: 'All Clients', initial: 'A' }
    : (() => {
        const c = clients.find((c: any) => c.id === state.selectedClient)
        return { name: c?.name || 'Select client', initial: c?.name?.[0]?.toUpperCase() || '?' }
      })()

  const isActive = (item: typeof navItems[0]) => {
    return item.matchPaths.some(p =>
      p === '/' ? location.pathname === '/' : location.pathname.startsWith(p)
    )
  }

  return (
    <nav
      className="w-full"
      style={{
        background: 'linear-gradient(180deg, rgba(30,30,32,0.97) 0%, rgba(18,18,20,0.98) 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 1px 3px rgba(0,0,0,0.3)',
      }}
    >
      <div className="flex items-center gap-0.5 px-5 max-w-full">
        {/* Brand mark */}
        <Link to="/dashboard" className="flex items-center pr-4 mr-2.5 py-1.5 shrink-0">
          <img
            src="/outboundbuddy-logo-white.png"
            alt="Outboundbuddy"
            className="w-10 h-10 object-contain"
          />
        </Link>

        {/* Nav items */}
        <div className="flex items-center gap-0.5 flex-1 min-w-0">
          {navItems.filter((item) => !item.adminOnly || isAdmin).map((item) => {
            const active = isActive(item)
            const baseStyle: React.CSSProperties = {
              padding: '14px 18px',
              color: active ? '#fff' : '#9ca3af',
              borderBottom: active ? '2px solid #e75127' : '2px solid transparent',
              letterSpacing: '0.02em',
            }
            const handlers = {
              onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
                if (!active) (e.target as HTMLElement).style.color = '#fff'
              },
              onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
                if (!active) (e.target as HTMLElement).style.color = '#9ca3af'
              },
            }
            if (item.external) {
              return (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-xs font-semibold transition-colors inline-flex items-center gap-1"
                  style={baseStyle}
                  {...handlers}
                >
                  {item.label}
                  <span style={{ fontSize: 10, opacity: 0.7 }}>↗</span>
                </a>
              )
            }
            return (
              <Link
                key={item.path}
                to={item.path}
                className="shrink-0 text-xs font-semibold transition-colors inline-flex items-center gap-1.5"
                style={baseStyle}
                {...handlers}
              >
                {item.label}
                {item.comingSoon && (
                  <span
                    title="Coming soon"
                    style={{
                      fontSize: 8,
                      fontWeight: 800,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      padding: '2px 5px',
                      borderRadius: 4,
                      background: 'rgba(129, 140, 248, 0.18)',
                      color: '#a5b4fc',
                      border: '1px solid rgba(129, 140, 248, 0.35)',
                      lineHeight: 1,
                    }}
                  >
                    Soon
                  </span>
                )}
              </Link>
            )
          })}

          {/* Old Pages moved into the profile menu (admin only), 26 Oct 2026. */}
        </div>

        {/* Right side: client selector + profile */}
        <div className="flex items-center gap-3 shrink-0">
          {/* Environment badge */}
          {import.meta.env.VITE_ENVIRONMENT === 'staging' && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-[10px] font-semibold rounded">
              STAGING
            </span>
          )}

          {/* Client selector — wireframe style: avatar + Client label + name + caret */}
          {isAdmin ? (
            <div className="relative">
              <button
                onClick={() => { setClientDropdownOpen(!clientDropdownOpen); setProfileDropdownOpen(false) }}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 rounded-lg transition-colors"
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                  style={{ background: '#e75127' }}
                >
                  {selectedClient.initial}
                </div>
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold leading-none">Client</span>
                  <span className="text-xs font-semibold text-white leading-tight max-w-[120px] truncate">{selectedClient.name}</span>
                </div>
                <span className="text-gray-500 text-[10px] ml-0.5">▾</span>
              </button>
              {clientDropdownOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 shadow-lg z-50 py-1"
                  style={{ borderRadius: '12px' }}
                >
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search clients..."
                        value={clientSearchTerm}
                        onChange={(e) => setClientSearchTerm(e.target.value)}
                        className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    <button
                      onClick={() => { setSelectedClient('all'); setClientDropdownOpen(false); setClientSearchTerm('') }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${state.selectedClient === 'all' ? 'text-blue-600 font-semibold' : 'text-gray-700'}`}
                    >
                      All Clients
                    </button>
                    {filteredClients.map((client: any) => (
                      <button
                        key={client.id}
                        onClick={() => { setSelectedClient(client.id); setClientDropdownOpen(false); setClientSearchTerm('') }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${state.selectedClient === client.id ? 'text-blue-600 font-semibold' : 'text-gray-700'}`}
                      >
                        {client.name}
                      </button>
                    ))}
                    {filteredClients.length === 0 && clientSearchTerm.trim() && (
                      <div className="px-3 py-4 text-center text-xs text-gray-400">No clients found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 px-2 py-1.5">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                style={{ background: workspace.loading ? '#6b7280' : '#e75127' }}
              >
                {/* While the client record resolves, show a neutral placeholder
                    instead of flashing a "W" / "My Workspace" — the client's own
                    name should never appear wrong, even for a beat. */}
                {workspace.loading ? '' : (workspace.client?.name || 'W')[0].toUpperCase()}
              </div>
              <div className="flex flex-col items-start">
                <span className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold leading-none">Client</span>
                {workspace.loading ? (
                  <span className="mt-0.5 h-3 w-16 rounded bg-white/20 animate-pulse" aria-label="Loading workspace" />
                ) : (
                  <span className="text-xs font-semibold text-white leading-tight">{workspace.client?.name || 'My Workspace'}</span>
                )}
              </div>
            </div>
          )}

          {/* Profile */}
          <div className="relative">
            <button
              onClick={() => { setProfileDropdownOpen(!profileDropdownOpen); setClientDropdownOpen(false) }}
              className="p-0.5 rounded-full hover:ring-2 hover:ring-white/20 transition"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-gray-700 text-white text-xs font-bold">
                  {profile?.full_name ? profile.full_name.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'U'}
                </AvatarFallback>
              </Avatar>
            </button>
            {profileDropdownOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 shadow-lg z-50"
                style={{ borderRadius: '12px' }}
              >
                <div className="p-3 border-b border-gray-100">
                  <p className="text-sm font-bold text-gray-900">{profile?.full_name || 'User'}</p>
                  <p className="text-xs text-gray-500">{profile?.email}</p>
                  <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    isAdmin ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'
                  }`}>
                    {profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1) : 'Client'}
                  </span>
                </div>
                <div className="p-1">
                  {dashboardLayout.canToggle && (
                    <div className="px-3 pt-2 pb-2.5 border-b border-gray-100 mb-1">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
                        <LayoutDashboard className="w-3.5 h-3.5 text-gray-400" />
                        Dashboard layout
                      </div>
                      <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5" role="group" aria-label="Dashboard layout">
                        {([
                          { value: 'v2', label: 'New' },
                          { value: 'classic', label: 'Classic' },
                        ] as const).map((opt) => {
                          const on = dashboardLayout.storedLayout === opt.value
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              aria-pressed={on}
                              onClick={() => dashboardLayout.setLayout(opt.value)}
                              className={`flex-1 px-2 py-1 text-xs font-semibold rounded-md transition ${
                                on ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:bg-white'
                              }`}
                            >
                              {opt.label}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => { navigate('/settings'); setProfileDropdownOpen(false) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition"
                  >
                    <Settings className="w-4 h-4 text-gray-400" />
                    Settings
                  </button>
                  {isAdmin && (
                    <>
                      <button
                        type="button"
                        aria-expanded={oldPagesOpen}
                        onClick={() => setOldPagesOpen(!oldPagesOpen)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition"
                      >
                        <History className="w-4 h-4 text-gray-400" />
                        <span className="flex-1 text-left">Old pages</span>
                        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${oldPagesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {oldPagesOpen && <div className="border-y border-gray-100 my-1"><OldPagesList onNavigate={() => { setProfileDropdownOpen(false); setOldPagesOpen(false) }} /></div>}
                    </>
                  )}
                  <button
                    onClick={() => { signOut(); setProfileDropdownOpen(false) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}
